// ========================================================================= //
// TRANSITION-PERIOD LOGIN NOTICE  (TEMPORARY — remove after transition)
// ------------------------------------------------------------------------- //
// This whole file is self-contained guidance shown on the reporting-portal
// login screen while users migrate to the new (MFA) login/registration flow.
//
// It is presentational only: it reuses the EXISTING register action passed in
// (goToRegister) and never touches auth logic.
//
// HOW TO TURN IT OFF:  set SHOW_TRANSITION_NOTICE below to false. LoginPage.js
//   guards every render call with this flag, so flipping it cleanly removes
//   all of the UI with no leftover gaps.
//
// HOW TO REMOVE PERMANENTLY (after the transition period):
//   1. Delete this file (TransitionLoginNotice.js).
//   2. In LoginPage.js, remove the import of this file and the handful of
//      `${SHOW_TRANSITION_NOTICE ? ... : ''}` render calls (all marked with a
//      "TRANSITION NOTICE" comment).
//   3. Delete the "TRANSITION-PERIOD LOGIN NOTICE" CSS block in css/main.scss.
//   4. Delete the two guide PDFs in static/docs/.
// ========================================================================= //

import { html } from 'neverland';

// Single on/off switch for the whole transition-period notice.
export const SHOW_TRANSITION_NOTICE = true;

// Served as-is from static/ (same mechanism as /static/img/... logos).
const QUICK_GUIDE_URL = '/static/docs/RYA_Reporting_Portal_Login_Quick_Guide_2026.pdf';
const DETAILED_GUIDE_URL = '/static/docs/RYA_Reporting_Portal_Login_and_Security_Setup_Detailed_Guide_2026.pdf';

const QUICK_GUIDE_LABEL = 'Quick guide: updated login instructions';
const DETAILED_GUIDE_LABEL = "Quick guide didn't help? See the full step-by-step guide";

// Prominent link to the QUICK guide, reused inside both top callouts.
function quickGuideLink() {
  return html`
    <a class="transition-notice-quicklink"
       href="${QUICK_GUIDE_URL}"
       target="_blank"
       rel="noopener noreferrer">
      <i class="fal fa-book-open"></i>&ensp;${QUICK_GUIDE_LABEL}
    </a>
  `;
}

// PAGE 1 (step 'org') — callout shown ABOVE the SCHOOL USERNAME field.
export function TransitionNoticeOrg() {
  return html`
    <div class="transition-notice">
      <p class="transition-notice-text">
        We've upgraded our portal login process to improve security.
        Please follow the prompts to set it up.
      </p>
      ${quickGuideLink()}
    </div>
  `;
}

// PAGE 2 (step 'credentials') — callout shown ABOVE the EMAIL/PASSWORD fields.
// `goToRegister` is the EXISTING register action from LoginPage.js; we reuse it
// so there is a single register path (no competing second handler).
export function TransitionNoticeCredentials(goToRegister) {
  return html`
    <div class="transition-notice">
      <p class="transition-notice-text">
        First time with the new setup?
        <button type="button" class="transition-notice-register-link" onclick=${goToRegister}>Register first</button>
        and follow the prompts. Already set up your new account?
        Continue with your email and password below.
      </p>
      ${quickGuideLink()}
    </div>
  `;
}

// BOTH PAGES — smaller, secondary fallback link shown BELOW the buttons.
export function TransitionNoticeFallback() {
  return html`
    <a class="transition-notice-fallback"
       href="${DETAILED_GUIDE_URL}"
       target="_blank"
       rel="noopener noreferrer">
      ${DETAILED_GUIDE_LABEL}
    </a>
  `;
}
